import { of } from 'rxjs/observable/of';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { NgZone, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { withLatestFrom, exhaustMap, switchMap, debounceTime, map, filter, tap, share, catchError } from 'rxjs/operators';

import { AppState } from '../../interfaces/app-state.interface';
import { ErrorsType } from '../../enum/errors.type';
import { RoutesTable } from '../../resources/routes-table.model';
import { BackendService } from '../../services/backend/backend.service';
import { TrackingService } from '../../services/tracking/tracking.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { TrackingActions } from './../tracking/tracking.action';
import { SuitabilityAction } from '../suitability/suitability.action';
import { UserProfileActions } from './userprofile.actions';
import { UserProfileService } from './../../services/users/user-profile.service';
import { BackendService as NewBackendService} from '../../services/new-http/backend.service';

@Injectable()
export class UserProfileEffects {

    /**
     * Atualiza um usuário.
     * @method updateUser
     */
    @Effect()
    public updateUser$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.update),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]) => {
                return this.userService.updateUser({ step: action.payload.step, value: action.payload.value })
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Warning, value: result.messages } };
                            }
                            if (result.hasError) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Warning, value: result.messages } };
                            }
                            if (result.hasValue) {
                                return { type: UserProfileActions.updateSuccess, payload: result.value };
                            }
                            if (result.ok) {
                                return { type: UserProfileActions.updateSuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.updateSuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateError, payload: error })));
            }),
            share());

    /**
     * Atualiza parte de um usuário.
     * @method updatePatchUser
     */
    @Effect()
    public updatePatchUser$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.updatePatch),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            exhaustMap(([action, store]) => {

                if (action.payload.isVirtualOnly) {
                    return of({
                        type: UserProfileActions.updateVirtualUser,
                        payload: { ...store.virtualUser, ...action.payload.value },
                    });
                }

                return this.userService.updatePatchUser(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.updatePatchSuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.updatePatchError, payload: { type: ErrorsType.Warning, value: result.messages } };

                        }),
                        catchError((error) => {
                            return of({ type: UserProfileActions.updatePatchError, payload: error });
                        }));
            }),
            share());

    /**
     * Atualiza parte de um usuário.
     * @method updateUserTags
     */
    @Effect()
    public updateUserTags$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.updateTags),
            switchMap((action) => {
                return this.userService.updateUserTags(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.updateTagsSuccess, payload: { userTag: action.payload.value } };
                            }

                            return { type: UserProfileActions.updateTagsError, payload: { type: ErrorsType.Warning, value: result.messages } };
                        }),
                        catchError((error) => of({ type: UserProfileActions.updateTagsError, payload: error })),
                    ).retry(3);
            }),
            share());

    /**
     * Atualiza uma empresa.
     * @method updateCompany
     */
    @Effect()
    public updateCompany$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.updateCompany),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]) => {
                return this.userService.updatePatchCompany(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: UserProfileActions.updateCompanyError,
                                    payload: { type: ErrorsType.Warning, value: result.messages },
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: UserProfileActions.updateCompanyError,
                                    payload: { type: ErrorsType.Warning, value: result.messages },
                                };
                            }
                            if (result.hasValue) {
                                return { type: UserProfileActions.updateCompanySuccess, payload: result.value };
                            }

                            if (result.ok) {
                                return { type: UserProfileActions.updateCompanySuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.updateCompanySuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateCompanyError, payload: error })));
            }),
            share());

    /**
     * Atualiza um usuário, mas que nescessesita de aprovação.
     * @method lazyUpdateUser
     */
    @Effect()
    public lazyUpdateUser$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.lazyUpdate),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            switchMap(([action, store]) => {
                return this.userService.updateUser({ step: action.payload.step, value: action.payload.value })
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Warning, value: result.messages } };
                            }
                            if (result.hasError) {
                                return { type: UserProfileActions.updateError, payload: { type: ErrorsType.Error, value: result.messages } };
                            }
                            if (result.hasValue) {
                                return { type: UserProfileActions.lazyUpdateSuccess, payload: result.value };
                            }

                            if (result.ok) {
                                return { type: UserProfileActions.lazyUpdateSuccess, payload: { ...store.user, ...action.payload.value } };
                            }

                            return { type: UserProfileActions.lazyUpdateSuccess, payload: result };

                        }),
                        catchError((error) => of({ type: UserProfileActions.updateError, payload: error })));
            }),
            share());

    @Effect({ dispatch: false })
    public sendRegistration$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.sendRegistration),
            withLatestFrom(this.store.select((state) => state.authentication.currentUser)),
            filter(([action, currentUser]) => action.payload && currentUser),
            tap(([action, currentuser]) => {
                this.tracking.alias(currentuser.userId);
            }));

    /**
     * Obtem um usuário em uma determinada rota/{{STEP}}.
     * @method getUser
     */
    @Effect()
    public getUser$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.getUser),
            withLatestFrom(this.store.select((state) => state.userProfile)),
            exhaustMap(([action, userProfileState]) => {

                if (userProfileState.pending === true) return of({ type: UserProfileActions.getttingUser });

                this.store.dispatch({ type: UserProfileActions.getttingUser });

                return this.userService.getUser()
                    .pipe(
                        map((result) => {
                            if (result.hasValue) {
                                const eventsObj = {
                                    name: result.value.name,
                                    email: result.value.email,
                                    cpfCnpj: result.value.cpfcnpj,
                                    phone: result.value.phoneAreaCode + result.value.phoneNumber,
                                };

                                this.userService.cacheUserProfileDataIfNeeded(result.value);

                                // this.store.dispatch({type: UserProfileActions.getTrader});

                                this.store.dispatch({
                                    type: TrackingActions.sendTrackLogin,
                                    payload: { userId: result.value.id, events: eventsObj },
                                });
                                return { type: UserProfileActions.getUserSuccess, payload: result.value };
                            }
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.getUserError, payload: result.messages };
                            }

                            return { type: UserProfileActions.getUserError, payload: result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getUserError, payload: error })));
            }),
            share());


     /**
     * Obtem um usuário em uma determinada rota/ baseando-se no CBLC do JWT Logado.
     * @method getUserByCblc
     */
        @Effect()
        public getUserByCBLC$: Observable<any> = this.actions$
            .pipe(
                ofType(UserProfileActions.getUserByCBLC),
                withLatestFrom(this.store.select((state) => {
                    return state.userProfile
                })),
                exhaustMap(([action, userProfileState]) => {
                    this.store.dispatch({ type: UserProfileActions.getUserByCBLC });

                    return this.userService.getUserByCBLC()
                    .pipe(
                        map((result) => {
                            if (result.hasValue) {
                                const eventsObj = {
                                    name: result.value.name,
                                    email: result.value.email,
                                    cpfCnpj: result.value.cpfcnpj,
                                    phone: result.value.phoneAreaCode + result.value.phoneNumber,
                                };
                                this.userService.cacheUserProfileDataIfNeeded(result.value);

                                this.store.dispatch({
                                    type: TrackingActions.sendTrackLogin,
                                    payload: { userId: result.value.id, events: eventsObj },
                                });
                                return { type: UserProfileActions.getUserSuccess, payload: result.value };
                            }
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.getUserError, payload: result.messages };
                            }

                            return { type: UserProfileActions.getUserError, payload: result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getUserError, payload: error })));
                }))

     /**
     * Obtem o cliente logado chamando a nova api escalável em lambda
     * @method getUserToroUser
     */
        @Effect()
        public getUserToroUser$: Observable<any> = this.actions$
            .pipe(
                ofType(UserProfileActions.getUserToroUser),
                withLatestFrom(this.store.select((state) => {
                    return state.userProfile
                })),
                exhaustMap(([action, userProfileState]) => {

                    this.store.dispatch({ type: UserProfileActions.getUserToroUser });

                    return this.userService.getUserToroUser()
                    .pipe(
                        map((result) => {
                            if (result.hasValue) {
                                const eventsObj = {
                                    name: result.value.name,
                                    email: result.value.email,
                                    cpfCnpj: result.value.cpfcnpj,
                                    phone: result.value.phoneAreaCode + result.value.phoneNumber,
                                };
                                this.userService.cacheUserProfileDataIfNeeded(result.value);

                                this.store.dispatch({
                                    type: TrackingActions.sendTrackLogin,
                                    payload: { userId: result.value.id, events: eventsObj },
                                });
                                return { type: UserProfileActions.getUserSuccess, payload: result.value };
                            }
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.getUserError, payload: result.messages };
                            }

                            return { type: UserProfileActions.getUserError, payload: result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getUserError, payload: error })));
                }))


    ///
    @Effect({ dispatch: false })
    public getUserProfileData$: Observable<UserProfileActions> =
        this.actions$
            .pipe(
                ofType(UserProfileActions.getCompany, UserProfileActions.getUserSuccess),
                debounceTime(500),
                tap((action) => {
                    this.store.dispatch({ type: SuitabilityAction.getProfile });
                }));

    /**
     * Obtem uma empresa em uma determinada rota/{{STEP}}.
     * @method getCompany
     */
    @Effect()
    public getCompany$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.getCompany),
            switchMap((actionPayload) => {
                return this.userService.getCompany()
                    .pipe(
                        map((result) => {
                            if (result.hasValue) {
                                return { type: UserProfileActions.getCompanySuccess, payload: result.value };
                            }
                            if (result.hasAlerta) {
                                return { type: UserProfileActions.getCompanyError, payload: result.messages };
                            }

                            return { type: UserProfileActions.getCompanyError, payload: result.messages };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getCompanyError, payload: error })));
            }),
            share());

    /** Recupera o modelo de risco do usuário */
    @Effect()
    public getRiskModel$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.getRiskModel),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.posRisk.routes.getRiskModel.uri)
                    .pipe(
                        map((response) => {

                            if (response.ok) {
                                return { type: UserProfileActions.getRiskModelSuccess, payload: response.value };
                            }

                            return { type: UserProfileActions.getRiskModelError, payload: response.value };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getRiskModelError, payload: error })));
            }),
            share());

    /** Recupera se o usuário pode trocar modelo de garantia ou não */
    @Effect()
    public getGarantyModel$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.getGarantyModel),
            switchMap((action) => {

                const payload = {
                    riskModel: action.payload,
                };

                return this.backendService.get<DefaultResponse>(
                    RoutesTable.composeUri(RoutesTable.posRisk.routes.getGarantyModel, payload.riskModel),
                )
                    .pipe(
                        map((response) => {
                            if (response.ok) {
                                return { type: UserProfileActions.getGarantyModelSuccess, payload: response.value };
                            }
                            return { type: UserProfileActions.getGarantyModelError, payload: response.value };
                        }),
                        catchError((error) => of({ type: UserProfileActions.getGarantyModelError, payload: error })));
            }),
            share());

    /** Atualiza o modelo do risco */
    @Effect()
    public updateRiskModel$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.updateRiskModel),
            switchMap((action: any) => {

                const payload = {
                    riskModel: action.payload,
                };

                return this.backendService
                    .post<DefaultResponse>(
                        RoutesTable.posRisk.routes.updateRiskModel.uri,
                        payload,
                        action.token,
                    )
                    .pipe(
                        map((result) => {
                            if (!result.ok) {
                                return { type: UserProfileActions.updateRiskModelError, payload: result.value };
                            }

                            return { type: UserProfileActions.updateRiskModelSuccess, payload: action.payload };
                        }),
                        catchError((error) => of({
                            type: UserProfileActions.updateRiskModelError,
                            payload: error,
                        })));
            }),
            share());

    /**
     * Atualiza o celular do cliente
     */
    @Effect()
    public updatePhone$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.updatePhone),
            switchMap((action: any) => {

                return this.backendService
                    .post<DefaultResponse>(
                        RoutesTable.userprofile.routes.updatePhone.uri,
                        action.payload,
                        action.token,
                    )
                    .pipe(
                        map((result) => {

                            if (!result || !result.ok) {
                                return { type: UserProfileActions.updatePhoneError, payload: result.messages };
                            }

                            return { type: UserProfileActions.updatePhoneSuccess, payload: result.ok };
                        }),
                        catchError((error) =>
                            of({ type: UserProfileActions.updateRiskModelError, payload: error }),
                        ));
            }),
            share());

    /**
     * Requisita rota para notificar sobre a origem do cadastro
     */
    @Effect()
    public notifyConclusion$: Observable<any> = this.actions$
        .pipe(
            ofType(UserProfileActions.notifyConclusion),
            switchMap((action: any) => {
                const url = RoutesTable.composeUri(RoutesTable.userprofile.routes.notifyConclusionToroUser, action.payload);
                return this.backendService.post<DefaultResponse>(url, null)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UserProfileActions.notifyConclusionSuccess, payload: result.ok };
                            }
                            return { type: UserProfileActions.notifyConclusionError, payload: result.messages };
                        }),
                        catchError((error) =>
                            of({ type: UserProfileActions.notifyConclusionError, payload: error }),
                        ));
            }),
            share());

    /**
     * Busca Perfil Trader do Usuário.
     * @method getTrader
     */
     @Effect()
     public getTrader$ = this.actions$
         .pipe(
             ofType(UserProfileActions.getTrader),
             switchMap((action) => {
                return this.newBackendService.api.get<DefaultResponse>('/trader')
                    .pipe(
                        map((result) => {
                            if (result) {
                                return { type: UserProfileActions.getTraderSuccess, payload: result };
                            }
                        }),
                        catchError((error) =>
                            of({ type: UserProfileActions.getTraderError, payload: error }),
                        ));
             }),
             share());

    constructor(
        private store: Store<AppState>,
        private actions$: Actions<UserProfileActions>,
        private userService: UserProfileService,
        private backendService: BackendService,
        private ngzone: NgZone,
        private tracking: TrackingService,
        private newBackendService: NewBackendService
    ) { }
}
